import Button from '@berlitz/button'
// import { DownloadCalendar } from '@berlitz/icons'
import Spacer from '@berlitz/spacer'
import Spinner from '@berlitz/spinner'
import PortalBerlitzUserEvents from '@classes/BerlitzUserEvents'
import { BorderlessAlert } from '@components/Alert'
import DateHeader from '@components/DateHeader'
import Datepicker from '@components/Datepicker'
import EmptyScheduledLessons from '@components/EmptyScheduledLessons'
// import { CircleLegend } from '@components/Legend'
import LessonCutOff from '@components/LessonCutOff'
import { BasicModal } from '@components/Modal'
import RoleBasedView, { usePortalFeatures } from '@components/RoleBasedView'
import ShowHoursButton from '@components/ShowHoursButton'
import WithLessonUtilsHOC, { WithLessonUtilsProp } from '@hocs/WithLessonUtils'
import WithUserTimeHOC, { WithUserTimeProps } from '@hocs/WithUserTime'
import { GroupClassListItem } from '@interfaces/MySchedule'
import { ProfileProps } from '@interfaces/UserProfile'
import { EventAttendeeContext } from '@layouts/Events/context'
import { uniqBy } from 'lodash'
import moment from 'moment'
import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Col, Row } from 'react-styled-flexboxgrid'
import TimelineSection from '../TimelineSection'
import { LayoutProp, LessonDate } from './interface'
import { ButtonBox, Container, DatePickerCol, SchedBox, SpinnerBox, SwitchWrapper } from './style'

const Layout: React.FC<LayoutProp & ProfileProps & WithLessonUtilsProp & WithUserTimeProps> = ({
  loading: propLoading,
  fetchingLessonDates,
  instructorId,
  currentDate,
  setCurrentDate,
  onMonthChange,
  lessonDates: propLessonDates = [],
  GroupClassList,
  profile,
  toUserDate,
  noConfirmDay,
  withPendingButtons,
  onPendingAccept = () => {},
  onPendingDecline = () => {},
  getUserDate,
  ...props
}) => {
  const intl = useIntl()
  const { state } = usePortalFeatures({
    configId: 'c::sidenav::events',
  })
  const userEventCtx = React.useContext(EventAttendeeContext)
  const [openModal, setOpenModal] = React.useState(false)
  const [_dayConfirmed, setDayConfirmed] = React.useState(false)
  const [showAll, setShowAll] = React.useState(false)
  const [lessonDates, setLessonDates] = React.useState<LessonDate[]>(propLessonDates)
  const [eventGroupClassList, setEventGroupClassList] = React.useState<GroupClassListItem[]>([])
  const loading = propLoading || userEventCtx.loading

  const hasUnregisteredLesson = props.hasUnregisteredLesson(GroupClassList, getUserDate(), toUserDate)
  const isAllLessonsCompleted = !hasUnregisteredLesson
  const incompleteMessage = intl.formatMessage({
    id: 'Please complete all open tasks from today in order to confirm your day.',
  })

  React.useEffect(() => {
    if (state.allowed) {
      const UserEvents = new PortalBerlitzUserEvents(userEventCtx.data?.getEventAttendeev2, profile?.Timezone)
      setLessonDates(UserEvents.getCalendarDates(uniqBy(propLessonDates, 'date')))
      setEventGroupClassList(UserEvents.getMyScheduleEventsByDate(currentDate))
    } else {
      setLessonDates(propLessonDates)
    }
  }, [state.allowed, userEventCtx.data?.getEventAttendeev2, profile?.Timezone, propLessonDates, currentDate])

  return (
    <SchedBox>
      <Row>
        <Col xs={12} lg={3}>
          <DateHeader date={currentDate} selected />
        </Col>
        <Col xs={12} lg={false}>
          <Spacer size="sm" />
        </Col>
        <Col xs={12} lg={6}>
          <RoleBasedView configId="c::instructor::myschedule::showall">
            <SwitchWrapper>
              <ShowHoursButton showAll={showAll} setShowAll={setShowAll} />
            </SwitchWrapper>
          </RoleBasedView>
        </Col>
        <DatePickerCol xs={false} lg={3}>
          {(fetchingLessonDates || userEventCtx.loading) && (
            <SpinnerBox>
              <Spinner size="sm" />
            </SpinnerBox>
          )}

          <Datepicker
            label=""
            value={currentDate
              .clone()
              .startOf('day')
              .format('DD MMMM YYYY')}
            dateFormat="dd MMMM yyyy"
            onSelect={setCurrentDate}
            selected={currentDate
              .clone()
              .startOf('day')
              .toDate()}
            onMonthChange={onMonthChange}
            isClearable={false}
            shouldCloseOnSelect={false}
            openToDate={currentDate
              .clone()
              .startOf('day')
              .toDate()}
            popperPlacement="bottom-end"
            popperModifiers={{
              flip: { behavior: ['bottom'] },
              preventOverflow: { enabled: false },
              hide: { enabled: false },
            }}
            dayClassName={(date: string) => {
              const lessonDay = lessonDates.find(lessonDate =>
                moment(lessonDate.date, 'YYYY-MM-DD').isSame(moment(date, moment.defaultFormat))
              )

              return lessonDay && `react-datepicker__day--highlighted-count-${lessonDay.numberOfLessons}`
            }}
            className="popped-over"
          />
        </DatePickerCol>
        <Col xs={12}>
          <Container>
            {/* <Row>
              <Col xs={12} lg={8}>
                <H2>
                  <FormattedMessage id="Today's schedule" />
                </H2>
              </Col>
              <Col xs={6} lg={2}>
                <Legends>
                  <CircleLegend color="green20">
                    <FormattedMessage id="Base" />
                  </CircleLegend>
                  <CircleLegend color="warning20">
                    <FormattedMessage id="Flex" />
                  </CircleLegend>
                </Legends>
              </Col>
              <Col xs={2} lg={false} />
              <Col xs={4} lg={2}>
                <CalendarButton>
                  <DownloadCalendar />
                  <FormattedMessage id="Download to calendar" />
                </CalendarButton>
              </Col>
            </Row> */}

            <Spacer size="md" />

            {(loading && GroupClassList.length < 1) ||
            (!loading && GroupClassList.length > 0) ||
            (loading && eventGroupClassList.length < 1) ||
            (!loading && eventGroupClassList.length > 0) ? (
              <LessonCutOff CurrentDate={currentDate} loading={loading}>
                {(isCutOff: boolean) => (
                  <>
                    <TimelineSection
                      loading={loading}
                      instructorId={instructorId}
                      currentDate={currentDate}
                      GroupClassList={[...GroupClassList, ...eventGroupClassList]}
                      isCutOff={isCutOff}
                      profile={profile}
                      withPendingButtons={withPendingButtons}
                      onAccept={onPendingAccept}
                      onDecline={onPendingDecline}
                      showAll={showAll}
                    />

                    {!noConfirmDay && (
                      <>
                        <Spacer size="lg" />
                        <ButtonBox>
                          <BorderlessAlert description={incompleteMessage} className="tip-alert" />
                          <Button onClick={() => setOpenModal(true)} disabled={loading || !isAllLessonsCompleted}>
                            <FormattedMessage id="Confirm day" />
                          </Button>
                        </ButtonBox>

                        <BasicModal
                          open={openModal}
                          onClose={() => {
                            setOpenModal(false)
                            if (isAllLessonsCompleted) {
                              setDayConfirmed(true)
                            }
                          }}
                          type={isAllLessonsCompleted ? 'success' : 'warning'}
                          modalTitle={
                            isAllLessonsCompleted ? intl.formatMessage({ id: 'Items submitted' }) : incompleteMessage
                          }
                        />
                      </>
                    )}
                  </>
                )}
              </LessonCutOff>
            ) : (
              <EmptyScheduledLessons />
            )}
          </Container>
        </Col>
      </Row>
    </SchedBox>
  )
}

export default WithUserTimeHOC(WithLessonUtilsHOC(Layout))
